/**
 * Accueil : hero en photos de marque (sections/accueil-hero.liquid).
 * Plusieurs photos : la barre de la photo affichée se remplit en CSS (durée réglée dans l'éditeur) et
 * la fin de son animation passe à la suivante. Pause : survol ou focus des commandes, section hors
 * écran, onglet caché, bouton pause, photo choisie dans l'éditeur. Avec « réduire les animations »,
 * rien ne défile tout seul (les barres et le glissement du doigt restent). Une seule photo : rien à faire.
 */
const reduction = window.matchMedia('(prefers-reduced-motion: reduce)');

class PsHero extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.ps-hero__slide')];
    this.n = this.slides.length;
    if (this.n < 2 || this.ready) return;
    this.ready = true;
    this.caps = [...this.querySelectorAll('.ps-hero__cap')];
    this.bars = [...this.querySelectorAll('.ps-hero__bar')];
    this.live = this.querySelector('.ps-hero__caps');
    this.pauseButton = this.querySelector('.ps-hero__pause');
    this.nav = this.querySelector('.ps-hero__nav');
    this.stage = this.querySelector('.ps-hero__stage');
    this.index = 0;
    this.state = { hover: false, focus: false, off: false, hidden: document.hidden, user: false, editor: false };
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.bars.forEach((bar, k) => bar.addEventListener('click', () => this.go(k), { signal }));
    this.bars.forEach((bar) => bar.querySelector('.ps-hero__fill').addEventListener('animationend', () => {
      if (this.running()) this.go(this.index + 1);
    }, { signal }));
    if (this.pauseButton) {
      this.pauseButton.hidden = !this.auto();
      this.pauseButton.addEventListener('click', () => this.set('user', !this.state.user), { signal });
    }
    this.nav.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.set('hover', true); }, { signal });
    this.nav.addEventListener('pointerleave', () => this.set('hover', false), { signal });
    this.nav.addEventListener('focusin', () => this.set('focus', true), { signal });
    this.nav.addEventListener('focusout', (e) => { if (!this.nav.contains(e.relatedTarget)) this.set('focus', false); }, { signal });
    document.addEventListener('visibilitychange', () => this.set('hidden', document.hidden), { signal });
    reduction.addEventListener('change', () => this.render(true), { signal });

    // Glisser du doigt (ou de la souris) sur la photo : photo suivante ou précédente.
    this.stage.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      this.start = { x: e.clientX, y: e.clientY };
    }, { signal });
    this.stage.addEventListener('pointerup', (e) => {
      const s = this.start;
      this.start = null;
      if (!s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
    }, { signal });
    this.stage.addEventListener('pointercancel', () => { this.start = null; }, { signal });
    this.stage.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        this.set('off', !entries[entries.length - 1].isIntersecting);
      });
      this.observer.observe(this);
    }

    // Éditeur de thème : la photo sélectionnée s'affiche et reste en place.
    document.addEventListener('shopify:block:select', (e) => {
      const k = this.slides.indexOf(e.target);
      if (k < 0) return;
      this.state.editor = true;
      this.go(k);
    }, { signal });
    document.addEventListener('shopify:block:deselect', (e) => {
      if (this.slides.includes(e.target)) this.set('editor', false);
    }, { signal });

    this.render(true);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.ready = false;
  }

  auto() {
    return this.dataset.auto !== 'false' && !reduction.matches;
  }

  running() {
    const s = this.state;
    return this.auto() && !s.hover && !s.focus && !s.off && !s.hidden && !s.user && !s.editor;
  }

  set(key, value) {
    if (this.state[key] === value) return;
    this.state[key] = value;
    this.render(false);
  }

  go(k) {
    this.index = (k + this.n) % this.n;
    this.render(true);
  }

  render(restart) {
    const i = this.index;
    const auto = this.auto();
    this.slides.forEach((slide, k) => {
      slide.classList.toggle('is-active', k === i);
      if (k === i) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    this.caps.forEach((cap) => { cap.hidden = Number(cap.dataset.index) !== i; });
    this.bars.forEach((bar, k) => {
      bar.classList.toggle('is-done', k < i);
      bar.classList.toggle('is-current', k === i);
      if (k === i) bar.setAttribute('aria-current', 'true');
      else bar.removeAttribute('aria-current');
      const fill = bar.querySelector('.ps-hero__fill');
      if (k !== i || !auto) {
        fill.classList.remove('is-run');
      } else if (restart || !fill.classList.contains('is-run')) {
        fill.classList.remove('is-run');
        void fill.offsetWidth; // relance l'animation depuis le début
        fill.classList.add('is-run');
      }
    });
    const s = this.state;
    this.classList.toggle('is-paused', !this.running());
    this.classList.toggle('is-stopped', s.user);
    if (this.pauseButton) {
      this.pauseButton.hidden = !auto;
      this.pauseButton.setAttribute('aria-label', s.user ? this.dataset.labelPlay : this.dataset.labelPause);
    }
    // Lecteurs d'écran : la légende n'est annoncée que si la photo ne change pas toute seule.
    if (this.live) this.live.setAttribute('aria-live', this.running() ? 'off' : 'polite');
  }
}

if (!customElements.get('ps-hero')) customElements.define('ps-hero', PsHero);

/**
 * Boutons du hero au toucher (22.10.2026, demande de Gillian) : l'icône passe en vert sur fond noir, comme au
 * survol sur ordinateur. Un appui au doigt est trop bref pour que :active se voie (et Safari iOS l'ignore) :
 * la classe is-press est posée au contact et gardée 1,2 s après le relâchement, le temps que la page suivante
 * se charge. Un défilement de la page l'annule ; au retour arrière (cache), les boutons sont remis à zéro.
 */
document.addEventListener('pointerdown', (e) => {
  if (e.pointerType === 'mouse') return;
  const cta = e.target.closest && e.target.closest('.ps-hero__cta');
  if (!cta) return;
  clearTimeout(cta.psPress);
  cta.classList.add('is-press');
  const fin = (ev) => {
    cta.removeEventListener('pointerup', fin);
    cta.removeEventListener('pointercancel', fin);
    if (ev.type === 'pointerup') cta.psPress = setTimeout(() => cta.classList.remove('is-press'), 1200);
    else cta.classList.remove('is-press');
  };
  cta.addEventListener('pointerup', fin);
  cta.addEventListener('pointercancel', fin);
}, { passive: true });
window.addEventListener('pageshow', () => {
  document.querySelectorAll('.ps-hero__cta.is-press').forEach((cta) => cta.classList.remove('is-press'));
});
